import { Link, UploadFile, Attachment, Close } from "@mui/icons-material";
import {
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Icon,
  IconButton,
  InputAdornment,
} from "@mui/material";
import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { FileUploadButton } from "./FileUploadButton";
import { FILE_TYPE_UPLOADED, FILE_TYPE_URL } from "./RadioWithFileReference";
import {
  ToggleButtonGroup as AquiloToggleButtonGroup,
  TextInputBase,
  Button as AquiloButton,
} from "@truevault/aquilo";
import { ArrowUpTrayIcon, LinkIcon, XMarkIcon } from "@heroicons/react/24/solid";

export const UrlOrFileInput: React.FC<{
  url: string;
  filename: string;
  error?: string;
  onUrlChanged: (newUrl: string) => void;
  onFileChanged: React.ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  showUploadedFile?: boolean;
  handleFileDelete?: () => void;
  optional?: boolean;
  useAquiloDesign?: boolean;
}> = ({
  url,
  onUrlChanged,
  filename,
  onFileChanged,
  disabled,
  error,
  showUploadedFile = false,
  handleFileDelete,
  optional = false,
  useAquiloDesign = false,
}) => {
  const [internalUrl, setInternalUrl] = useState(url);
  const [originalUrl, setOriginalUrl] = useState(url);
  const [isEditingUrl, setIsEditingUrl] = useState(false); // only used in aquilo design

  useEffect(() => {
    setInternalUrl(url);
    setOriginalUrl(url);

    setIsEditingUrl(!(url && url.length > 0)); // start in url editing mode if the url is missing/empty
  }, [url]);

  const hasFile = Boolean(filename);
  const [fileType, setFileType] = useState(() => (hasFile ? FILE_TYPE_UPLOADED : FILE_TYPE_URL));

  const persistUrlChange = (newUrl: string) => {
    if (newUrl != originalUrl) {
      onUrlChanged(newUrl);
    }

    setIsEditingUrl(false);
  };

  const debouncedPersistUrlChange = useCallback(_.debounce(persistUrlChange, 2000), []);

  const handleOnUrlChange = (e) => {
    const newUrl = e.target.value;
    switch (e.type) {
      case "change":
        setInternalUrl(newUrl);
        debouncedPersistUrlChange(newUrl);
        break;
      case "blur":
        debouncedPersistUrlChange.cancel();
        persistUrlChange(newUrl);
        break;
      case "keyup":
        if (e.keyCode === 13) {
          // user clicked "Enter"
          debouncedPersistUrlChange.cancel();
          persistUrlChange(newUrl);
        }
        break;
    }
  };

  let formFieldControl =
    fileType === FILE_TYPE_UPLOADED ? (
      <FileUploadButton
        onChange={onFileChanged}
        disabled={disabled}
        useAquiloDesign={useAquiloDesign}
      />
    ) : useAquiloDesign ? (
      <div className="flex items-center">
        {isEditingUrl ? (
          <>
            <div>
              <TextInputBase
                field="url"
                value={internalUrl ?? ""}
                disabled={disabled}
                onChange={handleOnUrlChange}
                onBlur={handleOnUrlChange}
                onKeyUp={handleOnUrlChange}
                placeholder="http://example.com/terms-of-service"
                type="text"
                required={false}
                inputHeight="h-[30px]"
                inputTextSize="text-[14px]"
              />
            </div>
            <div className="ml-[8px]">
              <AquiloButton
                customSizeClassname="h-[30px] w-auto text-[12px]"
                color="tertiary"
                onClick={() => persistUrlChange(internalUrl)}
              >
                Save Link
              </AquiloButton>
            </div>
          </>
        ) : (
          <>
            <div className="text-blue-600 text-[14px] font-medium leading-[21px] underline mr-[6px] max-w-[30ch] truncate text-ellipsis">
              <a href={url} target="_blank" rel="noopener noreferrer">
                {url}
              </a>
            </div>
            <div
              className="w-[24px] h-[24px] shrink-0 flex items-center justify-center rounded-full border border-solid border-neutral-300 hover:bg-neutral-100 hover:border-neutral-400 transition-all duration-200 cursor-pointer"
              onClick={() => setIsEditingUrl(true)}
            >
              <XMarkIcon className="w-[14px] h-[14px] text-neutral-500" />
            </div>
          </>
        )}
      </div>
    ) : (
      <TextField
        size="small"
        variant="outlined"
        fullWidth
        value={internalUrl ?? ""}
        error={Boolean(error)}
        disabled={disabled}
        onChange={(e) => setInternalUrl(e.target.value)}
        onKeyUp={(e) => {
          // Handle Enter key press
          if (e.keyCode === 13) {
            persistUrlChange(internalUrl);
          }
        }}
        placeholder="http://example.com/terms-of-service"
        InputProps={{ type: "text" }}
        InputLabelProps={{ required: false }}
        label={`${optional ? "(Optional) " : ""}URL to public documentation`}
        required
      />
    );

  if (useAquiloDesign) {
    return (
      <div className="flex items-center">
        {Boolean(filename) && showUploadedFile ? (
          <div className="flex items-center gap-[6px]">
            <div className="max-w-[30ch] truncate text-ellipsis text-blue-600 underline text-[14px] font-medium leading-[21px]">
              {filename}
            </div>
            <div
              className="w-[24px] h-[24px] shrink-0 flex items-center justify-center rounded-full border border-solid border-neutral-300 hover:bg-neutral-100 hover:border-neutral-400 transition-all duration-200 cursor-pointer"
              onClick={handleFileDelete}
            >
              <XMarkIcon className="w-[14px] h-[14px] text-neutral-500" />
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-[8px]">
              <AquiloToggleButtonGroup
                buttonOptions={[
                  {
                    content: <LinkIcon className="h-[14px] w-[14px]" />,
                    value: FILE_TYPE_URL,
                    disabled: disabled,
                  },
                  {
                    content: <ArrowUpTrayIcon className="h-[14px] w-[14px]" />,
                    value: FILE_TYPE_UPLOADED,
                    disabled: disabled,
                  },
                ]}
                selectedValue={fileType}
                onChange={(newType) => {
                  setFileType(newType);
                }}
                disabled={disabled}
              />

              {formFieldControl}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="d-flex flex-row align-items-center w-400">
      {!!filename && showUploadedFile ? (
        <TextField
          size="small"
          variant="outlined"
          disabled
          value={filename ?? ""}
          fullWidth
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <Icon>
                  <Attachment />
                </Icon>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleFileDelete} size="small" edge="end">
                  <Close />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      ) : (
        <>
          <ToggleButtonGroup
            size="small"
            value={fileType}
            onChange={(_e, newType) => setFileType(newType)}
            exclusive
            className="mr-sm"
            disabled={disabled}
          >
            <ToggleButton disabled={disabled} value={FILE_TYPE_URL}>
              <Tooltip title="Set a Link">
                <Link />
              </Tooltip>
            </ToggleButton>

            <ToggleButton disabled={disabled} value={FILE_TYPE_UPLOADED}>
              <Tooltip title="Upload a File">
                <UploadFile />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          {formFieldControl}
        </>
      )}
    </div>
  );
};
